import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiClient, Project } from "@/lib/api";
import { Campaign } from "@/types/campaign";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Target,
  Users,
  CheckCircle2,
  Clock,
  Briefcase,
  MapPin,
  Activity,
  Search,
  Filter,
  Grid,
  List,
  Star,
  Eye,
  Heart,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { format } from "date-fns";
import { useAuthStore } from "@/stores/authStore";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { DashboardProjectCard } from "@/components/DashboardProjectCard";
import { PROJECT_TV_RATINGS, getTVContentRatingLabel } from "@/types/brand-preferences";

// Mapping functions for display formatting
const formatAudienceDemographics = (demographics: string[]): string[] => {
  const mappings: Record<string, string> = {
    // Age ranges (IAB standard)
    'age_18_24': 'Ages 18-24',
    'age_25_34': 'Ages 25-34',
    'age_35_44': 'Ages 35-44',
    'age_45_54': 'Ages 45-54',
    'age_55_64': 'Ages 55-64',
    'age_65_plus': 'Ages 65+',
    
    // Legacy age group mappings
    'gen_z': 'Gen Z (18-24)',
    'millennials': 'Millennials (25-34)',
    'gen_x': 'Gen X (35-44)',
    'baby_boomers': 'Baby Boomers (65+)',
    'boomers': 'Baby Boomers (65+)',
    
    // Additional age ranges
    'adults_25_44': 'Adults 25-44',
    'adults_45_64': 'Adults 45-64',
    
    // Gender
    'male': 'Male',
    'female': 'Female',
    'balanced': 'Gender: Balanced',
    
    // Geographic/Lifestyle
    'urban': 'Urban',
    'suburban': 'Suburban',
    'rural': 'Rural',
    'tech_savvy': 'Tech-savvy',
    'high_income': 'High Income',
    'middle_income': 'Middle Income'
  };
  
  return demographics.map(demo => mappings[demo] || demo.charAt(0).toUpperCase() + demo.slice(1));
};

const formatSuitabilityFilters = (campaign_requirements: any): string[] => {
  const suitabilityInfo: string[] = [];
  
  if (!campaign_requirements) {
    return suitabilityInfo;
  }
  
  // Add TV content ratings if available (prioritize over safety filters)
  if (campaign_requirements.tv_content_ratings && Array.isArray(campaign_requirements.tv_content_ratings)) {
    const tvRatings = campaign_requirements.tv_content_ratings.map((rating: string) => {
      return getTVContentRatingLabel(rating as any) || rating;
    });
    suitabilityInfo.push(...tvRatings);
  } else {
    // Fallback to brand safety level if no TV ratings
    if (campaign_requirements.brand_safety_level) {
      const level = campaign_requirements.brand_safety_level;
      suitabilityInfo.push(`${level.charAt(0).toUpperCase() + level.slice(1)} Safety`);
    }
    
    // Add excluded content types if available
    if (campaign_requirements.excluded_content_types && Array.isArray(campaign_requirements.excluded_content_types)) {
      const contentFilters = campaign_requirements.excluded_content_types.map((filter: string) => {
        return filter.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
      });
      suitabilityInfo.push(...contentFilters);
    }
  }
  
  // Add geographic targets if available
  if (campaign_requirements.geographic_targets && Array.isArray(campaign_requirements.geographic_targets)) {
    const geoFilters = campaign_requirements.geographic_targets.map((target: string) => {
      return target.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    });
    suitabilityInfo.push(...geoFilters);
  }
  
  return suitabilityInfo;
};

const formatContentVerticals = (genres: string[]): string[] => {
  const mappings: Record<string, string> = {
    'comedy': 'Comedy',
    'drama': 'Drama',
    'tech': 'Technology',
    'technology': 'Technology',
    'thriller': 'Thriller',
    'action': 'Action',
    'romance': 'Romance',
    'horror': 'Horror',
    'documentary': 'Documentary',
    'animation': 'Animation',
    'sci_fi': 'Sci-Fi',
    'fantasy': 'Fantasy'
  };
  
  return genres.map(genre => mappings[genre] || genre.charAt(0).toUpperCase() + genre.slice(1));
};

// Function to get TV rating for a project
const getProjectTVRating = (project: Project): string | null => {
  return PROJECT_TV_RATINGS[project.title] || null;
};

// Function to check if project matches brand's TV rating preferences
const isProjectTVRatingCompatible = (project: Project, acceptableRatings: string[]): boolean => {
  if (!acceptableRatings || acceptableRatings.length === 0) {
    return true; // No restrictions means all ratings are acceptable
  }
  
  const projectRating = getProjectTVRating(project);
  if (!projectRating) {
    return true; // If project rating is not mapped, allow it (neutral stance)
  }
  
  return acceptableRatings.includes(projectRating);
};

// Function to check if project matches preferred genres
const isProjectGenreCompatible = (project: Project, preferredGenres: string[]): boolean => {
  if (!preferredGenres || preferredGenres.length === 0) {
    return true; // No genre restrictions means all genres are acceptable
  }
  
  const projectGenre = project.genre;
  if (!projectGenre) {
    return false; // No genre info means we can't confirm compatibility
  }
  
  // Handle both string and array project genres
  const projectGenres = Array.isArray(projectGenre) ? projectGenre : [projectGenre];
  
  // Check if any project genres match preferred genres (partial match)
  return projectGenres.some(pGenre =>
    preferredGenres.some(prefGenre =>
      pGenre.toLowerCase().includes(prefGenre.toLowerCase()) ||
      prefGenre.toLowerCase().includes(pGenre.toLowerCase())
    )
  );
};

// Function to categorize projects based on brand preferences
const categorizeProjects = (projects: Project[], campaignRequirements: any) => {
  const tvContentRatings = campaignRequirements?.tv_content_ratings || [];
  const preferredGenres = campaignRequirements?.preferred_genres || [];
  
  const goodMatch: Project[] = [];
  const otherProjects: Project[] = [];
  
  // If no preferences are specified, put all projects in "other" category
  if (tvContentRatings.length === 0 && preferredGenres.length === 0) {
    return { goodMatch: [], otherProjects: projects };
  }
  
  projects.forEach(project => {
    const projectRating = getProjectTVRating(project);
    const isTVRatingCompatible = projectRating && tvContentRatings.includes(projectRating);
    const isGenreCompatible = isProjectGenreCompatible(project, preferredGenres);
    
    // Consider it a good match if:
    // 1. TV rating is compatible (if TV ratings are specified) OR
    // 2. Genre is compatible (if genres are specified)
    const hasMatchingTVRating = tvContentRatings.length > 0 && isTVRatingCompatible;
    const hasMatchingGenre = preferredGenres.length > 0 && isGenreCompatible;
    
    if (hasMatchingTVRating || hasMatchingGenre) {
      goodMatch.push(project);
    } else {
      otherProjects.push(project);
    }
  });
  
  return { goodMatch, otherProjects };
};

// Function to render a project card with TV rating badge
const renderProjectCard = (project: Project, index: number, campaignId: string, navigate: any) => {
  // Generate different background styles for variety
  const backgroundStyles = [
    "linear-gradient(135deg, #fbbf24 0%, #60a5fa 100%)",
    "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
    "linear-gradient(135deg, #fdba74 0%, #f97316 100%)",
    "linear-gradient(135deg, #a78bfa 0%, #ec4899 100%)",
    "linear-gradient(135deg, #34d399 0%, #3b82f6 100%)",
    "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)"
  ];

  const bgStyle = backgroundStyles[index % backgroundStyles.length];
  
  // Check if project has assets and get the first non-deleted image asset as cover image
  const coverAsset = project.assets
    ?.find((asset) => !asset.deleted_at && (asset.content_type?.startsWith('image/') ?? false));
  const hasCoverImage = coverAsset && coverAsset.url;
  
  // Get TV rating for this project
  const tvRating = getProjectTVRating(project);

  return (
    <div
      key={project._id}
      className="bg-white rounded-xl overflow-hidden shadow-sm border border-neutral-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      onClick={() => navigate(`/campaigns/${campaignId}/projects/${project._id}/report`)}
    >
      <div className="h-48 relative overflow-hidden">
        {/* TV Rating Badge */}
        {tvRating && (
          <div className="absolute top-3 right-3 z-10">
            <Badge variant="secondary" className="bg-black/80 text-white font-semibold">
              {tvRating}
            </Badge>
          </div>
        )}
        
        {hasCoverImage ? (
          <img
            src={coverAsset.url}
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              // Fallback to gradient if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.style.background = bgStyle;
                parent.innerHTML = `
                  <div class="flex items-center justify-center h-full">
                    <div class="text-center text-white px-4">
                      <div class="text-2xl font-bold uppercase tracking-wide leading-tight">
                        ${project.title}
                      </div>
                    </div>
                  </div>
                  ${tvRating ? `<div class="absolute top-3 right-3 z-10"><span class="bg-black/80 text-white font-semibold px-2 py-1 rounded text-xs">${tvRating}</span></div>` : ''}
                `;
              }
            }}
          />
        ) : (
          <div
            className="h-full flex items-center justify-center"
            style={{ background: bgStyle }}
          >
            <div className="text-center text-white px-4">
              <div className="text-2xl font-bold uppercase tracking-wide leading-tight">
                {project.title}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-bold text-neutral-900">{project.title}</h3>
        </div>
        <p className="text-neutral-600 text-sm mb-4">
          {project.format || "Content"}
        </p>
        <p className="text-neutral-700 text-sm leading-relaxed mb-4">
          {(() => {
            const description = project.description || project.logline || "Behind-the-scenes look at breakthrough technologies and the teams developing solutions for tomorrow's challenges.";
            return description.length > 150 ? description.substring(0, 150) + "..." : description;
          })()}
        </p>

        {/* Project Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {(() => {
            const tags = [];
            const artieScore = (project as any).artie_score;
            const commercialViability = artieScore?.commercial_viability;

            // Get actual data from artie_score analysis
            if (commercialViability?.category_mapping?.primary_categories) {
              tags.push(...commercialViability.category_mapping.primary_categories.slice(0, 2));
            }

            // Add genre if available
            if (commercialViability?.category_mapping?.genre && tags.length < 3) {
              tags.push(commercialViability.category_mapping.genre);
            }

            // Add themes if we have space
            if (commercialViability?.category_mapping?.themes && tags.length < 4) {
              const remainingSlots = 4 - tags.length;
              tags.push(...commercialViability.category_mapping.themes.slice(0, remainingSlots));
            }

            // Add target demographics if we still have space
            if (commercialViability?.category_mapping?.target_demographics && tags.length < 4) {
              const remainingSlots = 4 - tags.length;
              tags.push(...commercialViability.category_mapping.target_demographics.slice(0, remainingSlots));
            }

            // Only show tags if we have actual data
            if (tags.length === 0) {
              return null;
            }

            // Limit to 4 tags max
            const displayTags = tags.slice(0, 4);

            return displayTags.map((tag, tagIndex) => (
              <span
                key={tagIndex}
                className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-full text-xs font-medium"
              >
                {tag}
              </span>
            ));
          })()}
        </div>

        {/* Placement Opportunities */}
        <div className="text-sm text-neutral-600">
          {(() => {
            const artieScore = (project as any).artie_score;
            const integrationOpportunities = artieScore?.integration_opportunities;

            if (integrationOpportunities) {
              const totalOpportunities =
                (integrationOpportunities.visible_logo_opportunities || 0) +
                (integrationOpportunities.product_mention_opportunities || 0) +
                (integrationOpportunities.product_use_opportunities || 0);

              if (totalOpportunities > 0) {
                const types = [];
                if (integrationOpportunities.product_mention_opportunities > 0) types.push("Narrative");
                if (integrationOpportunities.product_use_opportunities > 0) types.push("Product");
                if (integrationOpportunities.visible_logo_opportunities > 0) types.push("Logo");

                return (
                  <>
                    <span className="font-semibold">
                      {totalOpportunities} Placement Opportunities
                    </span>
                    {types.length > 0 && (
                      <>
                        <span className="mx-1">•</span>
                        <span>{types.slice(0, 2).join(", ")}</span>
                      </>
                    )}
                  </>
                );
              }
            }

            // Show message when no placement data is available
            return (
              <span className="text-neutral-500 italic">
                Placement analysis pending
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default function CampaignProjectsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { brand } = useAuthStore();
  // Project listing state
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOption, setSortOption] = useState("score");
  const [page, setPage] = useState(1);
  const limit = 6;
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const { data: campaign, isLoading, error: campaignError } = useQuery<Campaign>({
    queryKey: ["campaign", campaignId],
    queryFn: () => apiClient.getCampaign(campaignId!),
    enabled: !!campaignId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Calculate countdown to booking window end
  const bookingCountdown = useMemo(() => {
    const windowDetails = campaign?.window_details;
    if (!windowDetails?.submission_end_date) {
      return null;
    }

    // Use backend's is_active as primary source of truth (checks both status AND dates)
    // This ensures UI matches actual system behavior
    if (windowDetails.is_active === false) {
      return { expired: true };
    }

    const endDate = new Date(windowDetails.submission_end_date);
    const now = currentTime;
    const timeDiff = endDate.getTime() - now.getTime();

    // Fallback: also check date in case is_active wasn't updated
    if (timeDiff <= 0) {
      return { expired: true };
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    return {
      expired: false,
      days,
      hours,
      minutes,
      endDate,
      formattedEndDate: format(endDate, "MMMM d h:mm a 'EST'")
    };
  }, [campaign?.window_details, currentTime]);

  // Reset page and projects when search changes
  useEffect(() => {
    setPage(1);
    setAllProjects([]);
  }, [debouncedSearch, statusFilter, sortOption]);


  // State for background task management
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<'idle' | 'pending' | 'progress' | 'completed' | 'failed'>('idle');
  const [projectData, setProjectData] = useState<any>(null);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  // Function to trigger background ranking with fallback
  const triggerRanking = async () => {
    if (!campaignId) return;
    
    try {
      console.log('🚀 Triggering ranking for campaign:', campaignId);
      setTaskStatus('pending');
      setProjectsError(null);
      
      // Try background task approach first
      try {
        const response = await apiClient.triggerCampaignProjectRanking(campaignId);
        console.log('✅ Trigger response:', response);
        
        if (response.status === 'already_running') {
          console.log('⚠️ Task already running, falling back to direct endpoint');
          await fetchProjectsDirectly();
          return;
        }
        
        setTaskId(response.task_id);
        
        // Start SSE streaming for status updates with timeout
        streamTaskStatus(response.task_id);
        
        // Set a fallback timeout (30 seconds)
        setTimeout(() => {
          if (taskStatus === 'pending' || taskStatus === 'progress') {
            console.log('⏰ SSE timeout, falling back to direct endpoint');
            fetchProjectsDirectly();
          }
        }, 30000);
        
      } catch (taskError) {
        console.log('⚠️ Background task failed, falling back to direct endpoint:', taskError);
        await fetchProjectsDirectly();
      }
      
    } catch (error) {
      console.error('❌ Failed to trigger ranking:', error);
      setProjectsError(`Failed to start project ranking: ${JSON.stringify(error)}`);
      setTaskStatus('failed');
    }
  };

  // Function to fetch projects directly (checks cache first, triggers ranking if needed)
  const fetchProjectsDirectly = async () => {
    if (!campaignId) return;
    
    try {
      console.log('🔄 Checking for cached projects for campaign:', campaignId);
      setTaskStatus('progress');
      
      const response = await apiClient.getCampaignEligibleProjects(campaignId, 1, 1000); // Request up to 1000 projects
      console.log('✅ Direct fetch response:', response);
      console.log('📊 Campaign requirements in response:', response?.campaign_requirements);
      
      // Check if we got cached results with projects
      if (response && response.projects && response.projects.length > 0) {
        console.log('✅ Found cached projects, using cached results');
        setProjectData(response);
        setTaskStatus('completed');
      } else {
        console.log('⚠️ No cached projects found, triggering background ranking');
        // No cached results, trigger background ranking
        await triggerRanking();
      }
    } catch (error) {
      console.error('❌ Direct fetch failed:', error);
      setProjectsError(`Failed to fetch projects: ${JSON.stringify(error)}`);
      setTaskStatus('failed');
    }
  };

  // Function to stream task status via SSE
  const streamTaskStatus = (currentTaskId: string) => {
    if (!campaignId || !currentTaskId) return;
    
    console.log('🔄 Starting SSE stream for task:', currentTaskId);
    
    // Close existing EventSource if any
    if (eventSource) {
      eventSource.close();
    }
    
    // Create EventSource for real-time updates
    const newEventSource = new EventSource(
      `${import.meta.env.VITE_API_BASE_URL}/api/v1/campaigns/${campaignId}/projects/status/${currentTaskId}/stream`,
      {
        withCredentials: true
      }
    );
    
    setEventSource(newEventSource);
    
    newEventSource.onmessage = (event) => {
      try {
        const statusResponse = JSON.parse(event.data);
        console.log('🔍 SSE Status Response:', statusResponse);
        setTaskStatus(statusResponse.status);
        
        if (statusResponse.status === 'completed' && statusResponse.data) {
          console.log('✅ Task completed, data:', statusResponse.data);
          console.log('📊 SSE Campaign requirements in response:', statusResponse.data?.campaign_requirements);
          
          // Validate that we have the expected data structure
          if (statusResponse.data && typeof statusResponse.data === 'object' && statusResponse.data.success !== undefined) {
            setProjectData(statusResponse.data);
            setTaskStatus('completed');
            newEventSource.close();
            setEventSource(null);
          } else {
            console.error('❌ Invalid data structure received:', statusResponse.data);
            setProjectsError(`Invalid data received: ${JSON.stringify(statusResponse.data)}`);
            setTaskStatus('failed');
            newEventSource.close();
            setEventSource(null);
          }
        } else if (statusResponse.status === 'failed' || statusResponse.status === 'error') {
          setProjectsError(statusResponse.error || statusResponse.message || 'Task failed');
          setTaskStatus('failed');
          newEventSource.close();
          setEventSource(null);
        }
        // For pending/progress status, just update the status and continue listening
      } catch (error) {
        console.error('Failed to parse SSE data:', error);
        setProjectsError('Failed to parse status update');
        setTaskStatus('failed');
        newEventSource.close();
        setEventSource(null);
      }
    };
    
    newEventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      setProjectsError('Connection error during status updates');
      setTaskStatus('failed');
      newEventSource.close();
      setEventSource(null);
    };
  };

  // Cleanup EventSource on component unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        console.log('🧹 Cleaning up EventSource on unmount');
        eventSource.close();
        setEventSource(null);
      }
    };
  }, [eventSource]);

  // Check for cached results first, then trigger ranking if needed
  useEffect(() => {
    if (campaignId && taskStatus === 'idle') {
      // First try to fetch cached results directly
      fetchProjectsDirectly();
    }
  }, [campaignId]);

  // Reset when search/filter changes
  useEffect(() => {
    if (taskStatus === 'completed') {
      // For now, we'll re-trigger ranking when filters change
      // In a production app, you might want to implement client-side filtering
      setTaskStatus('idle');
      setProjectData(null);
      setTaskId(null);
    }
  }, [debouncedSearch, statusFilter, sortOption]);

  const projectsLoading = taskStatus === 'pending' || taskStatus === 'progress';

  // Use projects from background task result
  const projects = projectData?.projects || [];
  // Use the total from API response since backend already filters for reviewed projects
  const totalProjects = projectData?.total || projects.length;
  
  // Categorize projects based on brand preferences
  const { goodMatch, otherProjects } = useMemo(() => {
    return categorizeProjects(projects, projectData?.campaign_requirements);
  }, [projects, projectData?.campaign_requirements]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-900 mx-auto mb-4"></div>
          <p className="text-neutral-500">Loading campaign projects...</p>
        </div>
      </div>
    );
  }

  if (campaignError || !campaign) {
    return (
      <div className="min-h-screen bg-neutral-50 p-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-neutral-900 mb-4">Error Loading Campaign</h1>
          <p className="text-red-600 mb-8">{(campaignError as any)?.message || "Campaign not found"}</p>
          <Button onClick={() => navigate("/brand-dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (projectsError) {
    return (
      <div className="min-h-screen bg-neutral-50 p-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-neutral-900 mb-4">Error Loading Projects</h1>
          <p className="text-red-600 mb-8">{projectsError}</p>
          <Button onClick={() => {
            setTaskStatus('idle');
            setProjectsError(null);
            triggerRanking();
          }}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-geist">
      {/* Header */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to={`/campaigns/${campaignId}/browse-eligible`}
            className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            CAMPAIGN OVERVIEW
          </Link>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <h1 className="text-3xl font-bold text-neutral-900">Eligible Projects</h1>
              <Badge variant="secondary" className="font-normal text-lg px-3 py-1">
                {totalProjects}
              </Badge>
            </div>
            <p className="text-neutral-600 text-base mb-2">
              {/* Show different description based on whether brand requirements are provided */}
              {projectData?.campaign_requirements &&
               (projectData.campaign_requirements.target_demographics?.length > 0 ||
                projectData.campaign_requirements.preferred_genres?.length > 0 ||
                projectData.campaign_requirements.preferred_tones?.length > 0)
                ? 'Projects ranked by brand requirements mapping - audience demographics, content preferences, tone alignment, and integration potential.'
                : 'Projects ranked by brand fit analysis - safety, audience fit, integration potential, and content quality.'
              }
            </p>

            {/* Booking Window Timer */}
            <p className="text-neutral-600 text-base">
              {bookingCountdown ? (
                bookingCountdown.expired ? (
                  <span className="text-red-600 font-semibold">Booking Window Closed</span>
                ) : (
                  <>
                    Booking Window Closes In{" "}
                    <span className="font-semibold">
                      {bookingCountdown.days > 0 && `${bookingCountdown.days} day${bookingCountdown.days !== 1 ? 's' : ''}, `}
                      {bookingCountdown.hours} hour{bookingCountdown.hours !== 1 ? 's' : ''}
                    </span>{" "}
                    on {bookingCountdown.formattedEndDate}
                  </>
                )
              ) : (
                "Booking window information not available"
              )}
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0">

        {projectsLoading ? (
          /* Full Page Loader */
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-neutral-900"></div>
              <div className="absolute inset-0 rounded-full border-4 border-neutral-200"></div>
            </div>
            <div className="mt-6 text-center">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">Loading Eligible Projects</h3>
              <p className="text-neutral-600 max-w-md">
                We're analyzing and ranking projects based on your campaign requirements. This may take a moment...
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Filter Sections - Horizontal Layout */}
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6 mb-12">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* AUDIENCE Section */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wider mb-4">AUDIENCE</h3>
                  <div className="space-y-2">
                    {(() => {
                      console.log('🔍 Current projectData:', projectData);
                      console.log('🔍 Campaign requirements:', projectData?.campaign_requirements);
                      console.log('🔍 Target demographics:', projectData?.campaign_requirements?.target_demographics);
                      return null;
                    })()}
                    {projectData?.campaign_requirements?.target_demographics?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {formatAudienceDemographics(projectData.campaign_requirements.target_demographics).map((demographic: string, index: number) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-white border border-neutral-200 text-neutral-700 rounded-md text-sm font-medium"
                          >
                            {demographic}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-neutral-500 text-sm italic">No audience specified</span>
                    )}
                  </div>
                </div>

                {/* TONE Section */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wider mb-4">TONE</h3>
                  <div className="space-y-2">
                    {(() => {
                      const suitabilityInfo = formatSuitabilityFilters(projectData?.campaign_requirements);
                      
                      return suitabilityInfo.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {suitabilityInfo.map((info: string, index: number) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-white border border-neutral-200 text-neutral-700 rounded-md text-sm font-medium"
                            >
                              {info}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-neutral-500 text-sm italic">No suitability filters specified</span>
                      );
                    })()}
                  </div>
                </div>

                {/* CONTENT VERTICAL Section */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wider mb-4">CONTENT VERTICAL</h3>
                  <div className="space-y-2">
                    {projectData?.campaign_requirements?.preferred_genres?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {formatContentVerticals(projectData.campaign_requirements.preferred_genres).map((genre: string, index: number) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-white border border-neutral-200 text-neutral-700 rounded-md text-sm font-medium"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-neutral-500 text-sm italic">No content vertical specified</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Categorized Projects */}
            {projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
                  <Target className="w-8 h-8 text-neutral-400" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Eligible Projects Found</h3>
                <p className="text-neutral-500 font-geist max-w-md">
                  There are currently no projects that match this campaign's requirements.
                </p>
              </div>
            ) : (
              <>
                {/* Good Match Section */}
                {goodMatch.length > 0 && (
                  <div className="mb-12">
                    <div className="flex items-center gap-3 mb-6">
                      <h2 className="text-2xl font-bold text-neutral-900">Good Match</h2>
                      <Badge variant="secondary" className="font-normal text-lg px-3 py-1 bg-green-100 text-green-800">
                        {goodMatch.length}
                      </Badge>
                    </div>
                    <p className="text-neutral-600 text-base mb-6">
                      These projects match your brand's TV content rating preferences or preferred content genres.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {goodMatch.map((project, index) => renderProjectCard(project, index, campaignId!, navigate))}
                    </div>
                  </div>
                )}

                {/* Other Projects Section */}
                {otherProjects.length > 0 && (
                  <div className="mb-12">
                    <div className="flex items-center gap-3 mb-6">
                      <h2 className="text-2xl font-bold text-neutral-900">Other Projects</h2>
                      <Badge variant="secondary" className="font-normal text-lg px-3 py-1">
                        {otherProjects.length}
                      </Badge>
                    </div>
                    <p className="text-neutral-600 text-base mb-6">
                      These projects may not fully align with your TV content rating preferences but could still offer opportunities.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {otherProjects.map((project, index) => renderProjectCard(project, index + goodMatch.length, campaignId!, navigate))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* All projects are displayed at once - no pagination needed */}
          </>
        )}

      </main>
    </div>
  );
}